import React, { useState, useEffect, useCallback } from "react";
import apiClient from "@/api/client";
import { useNavigate } from "react-router-dom";
import {
  Users, CalendarClock, AlertTriangle, Clock, Building2,
  PauseCircle, UserX, TrendingUp, Euro, Percent, BarChart3, X, ExternalLink, RefreshCw, Mail, Send
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, ComposedChart, Line
} from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { formatDateEU } from "@/utils/dateFormat";
import { useAuth } from "@/contexts/AuthContext";

const PAYMENT_LABELS = { CARD: "Bancontact / myPOS", QR_BANCONTACT: "QR code Bancontact", TRANSFER: "Virement / Facture", CASH: "Cash / Numéraire", DOMICILIATION: "Domiciliation", OTHER: "FloaPay / Autre", BANCONTACT: "Bancontact (Payconiq)", MYPOS: "Boîtier MyPos", INVOICE: "Facture à prévoir" };
const paymentLabel = (v) => PAYMENT_LABELS[v] || v;

const KpiCard = ({ icon: Icon, label, value, color, onClick, suffix = "", subValue = null }) => (
  <div className={`crm-kpi-card crm-kpi-${color} ${onClick ? "hover:ring-2 hover:ring-offset-1 hover:ring-blue-300 transition-all" : ""}`}
    onClick={onClick} style={{ cursor: onClick ? "pointer" : "default" }}
    data-testid={`kpi-${label.replace(/\s+/g, '-').toLowerCase()}`}>
    <div className="crm-kpi-icon"><Icon size={22} /></div>
    <div className="crm-kpi-info">
      <span className="crm-kpi-value">{value}{suffix}</span>
      <span className="crm-kpi-label">{label}</span>
      {subValue && <span className="text-xs text-[#8fa4b5] mt-0.5">{subValue}</span>}
    </div>
  </div>
);

const StatCard = ({ label, value, color, onClick }) => (
  <div className={`bg-white rounded-lg p-4 border border-[#edf0f4] ${onClick ? "cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-blue-300 transition-all" : ""}`}
    onClick={onClick} data-testid={`stat-${label.replace(/\s+/g, '-').toLowerCase()}`}>
    <div className="text-xs text-[#8fa4b5] mb-1">{label}</div>
    <div className={`text-2xl font-bold ${color}`}>{value}</div>
  </div>
);

const CustomTooltip = ({ active, payload, label, type }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1a2744] text-white rounded-lg px-3 py-2 text-xs shadow-lg border border-[#2a3a5c]">
      <p className="font-medium mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>
          {p.name}: {type === "revenue" ? `${p.value?.toLocaleString()}€` : p.value}
        </p>
      ))}
    </div>
  );
};

const DashboardPage = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [financialData, setFinancialData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [sendingReport, setSendingReport] = useState(false);
  const navigate = useNavigate();

  const fetchDashboardData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      const [dashRes, finRes] = await Promise.all([
        apiClient.get("/dashboard"),
        apiClient.get("/dashboard/financial")
      ]);
      setData(dashRes.data);
      setFinancialData(finRes.data);
      setLastRefresh(new Date());
    } catch (err) {
      console.error("Dashboard fetch error:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData(false);
    // Auto-refresh every 60 seconds
    const interval = setInterval(() => fetchDashboardData(true), 60000);
    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  const handleManualRefresh = () => {
    fetchDashboardData(true);
  };

  const handleSendWeeklyReport = async () => {
    if (!window.confirm("Envoyer le rapport hebdomadaire maintenant ?\n\nDestinataire: elysee@bhealth.be\nCopie: sante@bhealth.be")) {
      return;
    }
    setSendingReport(true);
    try {
      const response = await apiClient.post("/reports/send-weekly");
      alert(`✅ ${response.data.message}\n\nEnvoyé à: ${response.data.to}\nCopie: ${response.data.cc}`);
    } catch (err) {
      console.error("Error sending report:", err);
      alert("❌ Erreur lors de l'envoi du rapport. Vérifiez les logs.");
    } finally {
      setSendingReport(false);
    }
  };

  const openDetail = async (type) => {
    setDetailLoading(true);
    setDetailOpen(true);
    try {
      const res = await apiClient.get(`/dashboard/financial/detail?type=${type}`);
      setDetailData(res.data);
    } catch (e) {
      console.error(e);
      setDetailData({ title: "Erreur", items: [], total_revenue: 0, count: 0 });
    } finally {
      setDetailLoading(false);
    }
  };

  if (loading) return <div className="crm-page-loading">Chargement du tableau de bord...</div>;
  if (!data) return <div className="crm-page-loading">Erreur de chargement</div>;

  const { kpis, monthly_expirations, urgent_actions } = data;
  const weeklyRev = financialData?.weekly_revenue || [];
  const monthlyRev = financialData?.monthly_revenue || [];
  const retention = financialData?.retention || { rate: 0, renewed: 0, expired: 0 };
  const current = financialData?.current_period || {};
  const memberStats = financialData?.member_stats || {};

  const weeklyChartData = weeklyRev.map(w => ({
    name: w.week, revenue: w.revenue, nouveaux: w.new_members, reconductions: w.renewals, total: w.inscriptions,
  }));
  const monthlyChartData = monthlyRev.map(m => ({
    name: `${m.month} ${m.year}`, revenue: m.revenue, nouveaux: m.new_members, reconductions: m.renewals, total: m.inscriptions,
  }));

  return (
    <div className="crm-page">
      <div className="crm-page-header">
        <div className="flex items-center justify-between w-full">
          <div>
            <h1 className="crm-page-title">Cockpit Management</h1>
            <p className="crm-page-subtitle">Vue d'ensemble de l'activité B.Health</p>
          </div>
          <div className="flex items-center gap-3">
            {lastRefresh && (
              <span className="text-xs text-[#8fa4b5]">
                Mise à jour : {lastRefresh.toLocaleTimeString("fr-BE", { hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
            {user?.role === "ADMIN" && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleSendWeeklyReport}
                disabled={sendingReport}
                className="text-[#8b5cf6] border-[#8b5cf6]/30 hover:bg-[#8b5cf6]/10"
                data-testid="send-weekly-report-btn"
              >
                <Mail size={14} className={`mr-1 ${sendingReport ? 'animate-pulse' : ''}`} />
                {sendingReport ? 'Envoi...' : 'Envoyer rapport'}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleManualRefresh}
              disabled={refreshing}
              className="text-[#3899ec] border-[#3899ec]/30 hover:bg-[#3899ec]/10"
              data-testid="refresh-dashboard-btn"
            >
              <RefreshCw size={14} className={`mr-1 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Actualisation...' : 'Actualiser'}
            </Button>
          </div>
        </div>
      </div>

      <div className="crm-kpi-grid">
        <KpiCard icon={Users} label="Membres actifs" value={kpis.active_members} color="blue" onClick={() => navigate("/members?status=ACTIVE")} />
        <KpiCard icon={CalendarClock} label="Échéances J-30" value={kpis.expiring_30} color="orange" onClick={() => navigate("/expirations?filter=j30")} />
        <KpiCard icon={AlertTriangle} label="Échéances J-15" value={kpis.expiring_15} color="red" onClick={() => navigate("/expirations?filter=j15")} />
        <KpiCard icon={Clock} label="Expirés ce mois" value={kpis.expired_this_month} color="gray" onClick={() => navigate("/expirations?filter=expired")} />
        <KpiCard icon={Building2} label="Domiciliations actives" value={kpis.domiciliations} color="yellow" onClick={() => navigate("/expirations?filter=domiciliation")} subValue={kpis.domiciliations_inactive > 0 ? `(+${kpis.domiciliations_inactive} inactives)` : null} />
        <KpiCard icon={PauseCircle} label="Suspensions" value={kpis.suspensions} color="purple" />
        <KpiCard icon={UserX} label="Membres inactifs" value={kpis.former_members} color="red" />
        <KpiCard icon={TrendingUp} label="Total membres" value={kpis.total_members} color="teal" />
      </div>

      {/* Financial KPIs - CLICKABLE */}
      <h2 className="text-lg font-semibold text-[#162d3d] mt-8 mb-4 flex items-center gap-2">
        <BarChart3 size={20} className="text-[#3899ec]" /> Dashboard Financier
      </h2>
      <div className="crm-kpi-grid">
        <KpiCard icon={Euro} label="Revenus semaine" value={current.week_revenue?.toLocaleString() || 0} suffix="€" color="green" onClick={() => openDetail("week_revenue")} />
        <KpiCard icon={Euro} label="Revenus mois" value={current.month_revenue?.toLocaleString() || 0} suffix="€" color="green" onClick={() => openDetail("month_revenue")} />
        <KpiCard icon={Users} label="Inscriptions semaine" value={current.week_inscriptions || 0} color="blue" onClick={() => openDetail("week_inscriptions")} />
        <KpiCard icon={Percent} label="Taux de rétention" value={retention.rate || 0} suffix="%" color={retention.rate >= 70 ? "green" : retention.rate >= 50 ? "orange" : "red"} onClick={() => openDetail("retention_renewed")} />
      </div>

      {/* New vs Renewals Stats - CLICKABLE */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
        <StatCard label="Semaine - Nouveaux" value={weeklyRev[weeklyRev.length-1]?.new_members || 0} color="text-[#22c55e]" onClick={() => openDetail("week_new")} />
        <StatCard label="Semaine - Reconductions" value={weeklyRev[weeklyRev.length-1]?.renewals || 0} color="text-[#f59e0b]" onClick={() => openDetail("week_renewals")} />
        <StatCard label="Mois - Nouveaux" value={monthlyRev[monthlyRev.length-1]?.new_members || 0} color="text-[#22c55e]" onClick={() => openDetail("month_new")} />
        <StatCard label="Mois - Reconductions" value={monthlyRev[monthlyRev.length-1]?.renewals || 0} color="text-[#f59e0b]" onClick={() => openDetail("month_renewals")} />
      </div>

      {/* CHARTS SECTION */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <div className="bg-white rounded-xl border border-[#edf0f4] p-5" data-testid="chart-weekly-revenue">
          <h3 className="text-sm font-semibold text-[#162d3d] mb-4">Revenus - 8 dernières semaines</h3>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={weeklyChartData}>
              <defs><linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3899ec" stopOpacity={0.3} /><stop offset="95%" stopColor="#3899ec" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#edf0f4" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#8fa4b5" }} />
              <YAxis tick={{ fontSize: 11, fill: "#8fa4b5" }} tickFormatter={v => `${v}€`} />
              <Tooltip content={<CustomTooltip type="revenue" />} />
              <Area type="monotone" dataKey="revenue" name="Revenus" stroke="#3899ec" strokeWidth={2.5} fill="url(#revGrad)" dot={{ r: 4, fill: "#3899ec", stroke: "#fff", strokeWidth: 2 }} activeDot={{ r: 6 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-xl border border-[#edf0f4] p-5" data-testid="chart-monthly-revenue">
          <h3 className="text-sm font-semibold text-[#162d3d] mb-4">Revenus - 6 derniers mois</h3>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={monthlyChartData}>
              <defs><linearGradient id="revGradM" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} /><stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#edf0f4" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#8fa4b5" }} />
              <YAxis tick={{ fontSize: 11, fill: "#8fa4b5" }} tickFormatter={v => `${v}€`} />
              <Tooltip content={<CustomTooltip type="revenue" />} />
              <Area type="monotone" dataKey="revenue" name="Revenus" stroke="#8b5cf6" strokeWidth={2.5} fill="url(#revGradM)" dot={{ r: 4, fill: "#8b5cf6", stroke: "#fff", strokeWidth: 2 }} activeDot={{ r: 6 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-xl border border-[#edf0f4] p-5" data-testid="chart-weekly-inscriptions">
          <h3 className="text-sm font-semibold text-[#162d3d] mb-4">Inscriptions - 8 dernières semaines</h3>
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart data={weeklyChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#edf0f4" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#8fa4b5" }} />
              <YAxis tick={{ fontSize: 11, fill: "#8fa4b5" }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="nouveaux" name="Nouveaux" stackId="a" fill="#22c55e" radius={[0, 0, 0, 0]} />
              <Bar dataKey="reconductions" name="Reconductions" stackId="a" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              <Line type="monotone" dataKey="total" name="Tendance" stroke="#1a2744" strokeWidth={2} strokeDasharray="5 5" dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-xl border border-[#edf0f4] p-5" data-testid="chart-monthly-inscriptions">
          <h3 className="text-sm font-semibold text-[#162d3d] mb-4">Inscriptions - 6 derniers mois</h3>
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart data={monthlyChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#edf0f4" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#8fa4b5" }} />
              <YAxis tick={{ fontSize: 11, fill: "#8fa4b5" }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="nouveaux" name="Nouveaux" stackId="a" fill="#22c55e" radius={[0, 0, 0, 0]} />
              <Bar dataKey="reconductions" name="Reconductions" stackId="a" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              <Line type="monotone" dataKey="total" name="Tendance" stroke="#1a2744" strokeWidth={2} strokeDasharray="5 5" dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Retention Card */}
      <div className="bg-white rounded-xl border border-[#edf0f4] p-5 mt-6">
        <h3 className="text-sm font-semibold text-[#162d3d] mb-4">Taux de Rétention</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <p className="text-xs text-[#8fa4b5] mb-2 uppercase tracking-wide font-medium">Ce mois-ci</p>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-[#577083]">Restent membres / Partent</span>
              <span className={`text-2xl font-bold ${(retention.month?.rate || 0) >= 70 ? 'text-green-600' : (retention.month?.rate || 0) >= 50 ? 'text-[#f59e0b]' : 'text-red-500'}`}>{retention.month?.rate || 0}%</span>
            </div>
            <div className="h-3 bg-[#edf0f4] rounded-full overflow-hidden">
              <div className={`h-full transition-all duration-500 ${(retention.month?.rate || 0) >= 70 ? 'bg-green-500' : (retention.month?.rate || 0) >= 50 ? 'bg-[#f59e0b]' : 'bg-red-500'}`} style={{ width: `${retention.month?.rate || 0}%` }} />
            </div>
            <div className="flex justify-between mt-2 text-xs text-[#8fa4b5]">
              <span className="text-green-600">{retention.month?.renewed || 0} restent</span>
              <span className="text-red-500">{retention.month?.not_renewed || 0} partent</span>
              <span>Total: {retention.month?.total || 0}</span>
            </div>
          </div>
          <div>
            <p className="text-xs text-[#8fa4b5] mb-2 uppercase tracking-wide font-medium">Année en cours</p>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-[#577083]">Restent membres / Partent</span>
              <span className={`text-2xl font-bold ${(retention.year?.rate || 0) >= 70 ? 'text-green-600' : (retention.year?.rate || 0) >= 50 ? 'text-[#f59e0b]' : 'text-red-500'}`}>{retention.year?.rate || 0}%</span>
            </div>
            <div className="h-3 bg-[#edf0f4] rounded-full overflow-hidden">
              <div className={`h-full transition-all duration-500 ${(retention.year?.rate || 0) >= 70 ? 'bg-green-500' : (retention.year?.rate || 0) >= 50 ? 'bg-[#f59e0b]' : 'bg-red-500'}`} style={{ width: `${retention.year?.rate || 0}%` }} />
            </div>
            <div className="flex justify-between mt-2 text-xs text-[#8fa4b5]">
              <span className="text-green-600 cursor-pointer hover:underline" onClick={() => openDetail("retention_renewed")}>{retention.year?.renewed || 0} restent</span>
              <span className="text-red-500 cursor-pointer hover:underline" onClick={() => openDetail("retention_not_renewed")}>{retention.year?.not_renewed || 0} partent</span>
              <span>Total: {retention.year?.total || 0}</span>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 text-center mt-4">
          <div className="bg-green-50 rounded-lg p-3"><div className="text-2xl font-bold text-green-600">{memberStats.active || 0}</div><div className="text-xs text-[#577083]">Actifs</div></div>
          <div className="bg-red-50 rounded-lg p-3"><div className="text-2xl font-bold text-red-500">{memberStats.former || 0}</div><div className="text-xs text-[#577083]">Anciens</div></div>
        </div>
      </div>

      {/* Bottom: Expirations forecast + Urgent actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <div className="bg-white rounded-xl border border-[#edf0f4] p-5" data-testid="chart-expiration-forecast">
          <h3 className="text-sm font-semibold text-[#162d3d] mb-4">Échéances à venir (4 mois)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={monthly_expirations || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#edf0f4" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#8fa4b5" }} />
              <YAxis tick={{ fontSize: 11, fill: "#8fa4b5" }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" name="Échéances" fill="#f97316" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-xl border border-[#edf0f4] p-5" data-testid="urgent-actions">
          <h3 className="text-sm font-semibold text-[#162d3d] mb-4">
            Actions prioritaires <span className="text-xs font-normal text-[#8fa4b5] ml-1">(échéances J-15)</span>
          </h3>
          {urgent_actions.length === 0 ? (
            <div className="text-center py-8 text-[#8fa4b5]">
              <CalendarClock size={32} className="mx-auto mb-2 text-[#c1c9d0]" />
              <p className="text-sm">Aucune action urgente</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
              {urgent_actions.map((a) => (
                <div key={a.id} className="flex items-center justify-between p-2.5 rounded-lg hover:bg-[#f4f7fa] cursor-pointer transition-colors border border-transparent hover:border-[#edf0f4]"
                  onClick={() => navigate(`/members/${a.member_id}`)} data-testid={`urgent-${a.id}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${a.days_remaining < 0 ? 'bg-red-500 animate-pulse' : a.days_remaining <= 7 ? 'bg-red-500' : 'bg-[#f59e0b]'}`} />
                    <div>
                      <span className="text-sm font-medium text-[#162d3d] block">{a.member_name}</span>
                      <span className="text-xs text-[#8fa4b5]">{a.plan_name}</span>
                    </div>
                  </div>
                  <span className={`text-sm font-bold px-2.5 py-1 rounded-full ${a.days_remaining < 0 ? 'bg-red-50 text-red-600' : a.days_remaining <= 7 ? 'bg-red-50 text-red-600' : 'bg-orange-50 text-[#f59e0b]'}`}>
                    {a.days_remaining < 0 ? `Expiré (${Math.abs(a.days_remaining)}j)` : `J-${a.days_remaining}`}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="sm:max-w-[800px] max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle data-testid="detail-modal-title">{detailData?.title || "Détail"}</DialogTitle>
          </DialogHeader>
          {detailLoading ? (
            <div className="py-10 text-center text-[#8fa4b5]">Chargement...</div>
          ) : detailData ? (
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="flex items-center gap-4 mb-4 px-1">
                <span className="text-sm text-[#577083]" data-testid="detail-count"><strong>{detailData.count}</strong> {detailData.count > 1 ? "entrées" : "entrée"}</span>
                {detailData.total_revenue > 0 && (
                  <span className="text-sm font-semibold text-green-600" data-testid="detail-total">Total: {detailData.total_revenue.toLocaleString()}€</span>
                )}
              </div>
              <div className="flex-1 overflow-auto border rounded-lg">
                <table className="w-full text-sm" data-testid="detail-table">
                  <thead className="bg-[#f4f7fa] sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2 text-xs font-medium text-[#577083]">Membre</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-[#577083]">Plan</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-[#577083]">Prix</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-[#577083]">Paiement</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-[#577083]">Date début</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-[#577083]">Type</th>
                      <th className="text-center px-3 py-2 text-xs font-medium text-[#577083]"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailData.items.map((item, idx) => (
                      <tr key={idx} className="border-t border-[#edf0f4] hover:bg-[#f4f7fa]">
                        <td className="px-3 py-2">
                          <div className="font-medium text-[#162d3d]">{item.member_name}</div>
                          {item.entry_type === "manual" && item.coach_name
                            ? <div className="text-xs text-[#8fa4b5]">par {item.coach_name}</div>
                            : item.member_email && <div className="text-xs text-[#8fa4b5]">{item.member_email}</div>}
                        </td>
                        <td className="px-3 py-2 text-[#577083]">{item.plan_name}</td>
                        <td className="px-3 py-2 font-medium">{item.price_eur}€</td>
                        <td className="px-3 py-2 text-[#577083] text-xs">{paymentLabel(item.payment_type)}</td>
                        <td className="px-3 py-2 text-[#577083] text-xs">{formatDateEU(item.start_date)}</td>
                        <td className="px-3 py-2">
                          {item.entry_type === "manual"
                            ? <span className="crm-badge bg-purple-100 text-purple-700 text-xs">Entrée manuelle</span>
                            : item.is_new
                              ? <span className="crm-badge bg-green-100 text-green-700 text-xs">Nouveau</span>
                              : <span className="crm-badge bg-[#f59e0b]/10 text-[#f59e0b] text-xs">Reconduction</span>}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {item.member_id && (
                            <Button variant="ghost" size="sm" onClick={() => { setDetailOpen(false); navigate(`/members/${item.member_id}`); }} data-testid={`detail-goto-${idx}`}>
                              <ExternalLink size={14} className="text-[#3899ec]" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {detailData.items.length === 0 && (
                      <tr><td colSpan={7} className="px-3 py-8 text-center text-[#8fa4b5]">Aucune donnée pour cette période</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardPage;
